import React, { useState, useEffect, useContext } from 'react';
import { ThemeProvider, ThemeContext, unityTheme, unityDarkTheme, Page } from 'react-unity';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './components/Home/Home';
import SubscriptionsHome from './components/Subscriptions/SubscriptionsHome/SubscriptionsHome';
import AdAppsHome from './components/AdApps/Apps/AdAppsHome';
import AdAppsDetail from './components/AdApps/Apps/AdAppsDetail';
import AdAppsImport from './components/AdApps/Apps/AdAppsImport';
import DecideTransferAppOwnership from './components/AdApps/components/DecideTransferAppOwnership';
import CloudAccountsHome from './components/CloudAccounts/CloudAccountsHome';
import ConfirmPasswordReset from './components/CloudAccounts/ConfirmPasswordReset';
import CloudGroupsHome from './components/CloudGroups/CloudGroupsHome';
import PortOpeningRequestsHome from './components/PortOpeningRequests/PortOpeningRequestsHome';
import OnPremPortOpeningRequestsHome from './components/OnPremPortOpeningRequests/OnPremPortOpeningRequestsHome';
import DoNotDecryptRequestsHome from './components/DoNotDecryptRequests/DoNotDecryptRequestsHome';
import PortOpeningRequestsCreate from './components/PortOpeningRequests/PortOpeningRequestsCreate';
import OnPremPortOpeningRequestsCreate from './components/OnPremPortOpeningRequests/OnPremPortOpeningRequestsCreate';
import OnPremPortOpeningRequestsDetail from './components/OnPremPortOpeningRequests/OnPremPortOpeningRequestsDetail';
import DoNotDecryptRequestsCreate from './components/DoNotDecryptRequests/DoNotDecryptRequestsCreate';
import PortOpeningRequestsHistory from './components/PortOpeningRequests/PortOpeningRequestsHistory';
import DoNotDecryptRequestsHistory from './components/DoNotDecryptRequests/DoNotDecryptRequestsHistory';
import { PortOpeningRequestsDirection } from './models/enums/POR/PortOpeningRequestsDirection';
import DoNotDecryptRequestsDetail from './components/DoNotDecryptRequests/DoNotDecryptRequestsDetail';
import { DoNotDecryptRequestsDirection } from './models/enums/DNDR/DoNotDecryptRequestsDirection';
import PortOpeningRequestsDetail from './components/PortOpeningRequests/PortOpeningRequestsDetail';
import FeedbackHome from './components/Feedback/FeedbackHome';
import SubscriptionsDetail from './components/Subscriptions/SubscriptionsDetail';
import SubscriptionsRequest from './components/Subscriptions/SubscriptionsRequest';
import SubscriptionRulesTable from './components/Subscriptions/SubscriptionRulesTable';
import SelfAttestation from './components/Subscriptions/SelfAttestation/SelfAttestationHome';
import SwaggerHome from './components/Swagger/SwaggerHome';
//import PolicyExemptionRequestsHome from './components/PolicyExemption/PolicyExemptionRequestsHome';
//import PolicyExemptionRequestsCreate from './components/PolicyExemption/PolicyExemptionRequestsCreate';
//import PolicyExemptionRequestsDetail from './components/PolicyExemption/PolicyExemptionRequestDetail';
import SubAdminConsent from './components/Subscriptions/AdminConsent/SubAdminConsent';
import OrganizationsHome from './components/Organizations/OrganizationsHome/OrganizationsHome';
import TagManager from 'react-gtm-module';
import SubscriptionsImport from './components/Subscriptions/SubscriptionsImport';
import VirtualMachineRequest from './components/VirtualMachineRequest/VirtualMachineRequestCreate';
import VirtualMachineRequestHistory from './components/VirtualMachineRequest/VirtualMachineRequestHistory';
import VirtualMachineRequestDetail from './components/VirtualMachineRequest/VirtualMachineRequestDetail';
import ASWPortOpeningRequestsCreate from './components/AWSPortOpeningRequests/AWSPortOpeningRequestsCreate';
import AWSPortOpeningRequestsDetail from './components/AWSPortOpeningRequests/AWSPortOpeningRequestsDetail';
import AWSPortOpeningRequestsHome from './components/AWSPortOpeningRequests/AWSPortOpeningRequestsHome';
import TemporaryAccessPassword from './components/TemporaryAccessPass/TemporaryAccessPassword';
import ApprovalsHome from './components/Approvals/ApprovalsHome';

const tagManagerArgs = {
	gtmId: 'G-1GMQYF6KHL'
}

const App = () => {
	const { setTheme } = useContext(ThemeContext);
	if (process.env.REACT_APP_NODE_GA_ENV === 'production') {
		TagManager.initialize(tagManagerArgs);
	}
	const [themeName, setThemeName] = useState(() => {
		const themeName = localStorage.getItem('theme');
		if (!themeName) return 'default';
		return themeName;
	});

	useEffect(() => {
		setTheme(themeName === 'default' ? unityTheme : unityDarkTheme);
	}, [setTheme, themeName]);

	const handleThemeChange = () => {
		setThemeName((previousThemeName) => {
			const newThemeName = previousThemeName === 'default' ? 'dark' : 'default';
			localStorage.setItem('theme', newThemeName);
			return newThemeName;
		});
	};

	const RedirectRoute = (from: string, to: string) => (
		<Route path={from}  element={<Navigate to={to} />} />
	);

	return (
		<ThemeProvider theme={themeName === 'default' ? unityTheme : unityDarkTheme}>
			<BrowserRouter>
				<Page>
					<Page.Header>
						<Header currentTheme={themeName} onThemeChange={handleThemeChange} />
					</Page.Header>
					<Page.Body>
						<main role="main" className="main-content-container min-height">
							<Routes>
								<Route
									path="/"
									element={<Home />}
								/>
								<Route
									path="/swagger"
									element={<SwaggerHome />}
								/>
								{RedirectRoute('/swagger/index.html', '/swagger')}
								<Route
									path="/adApps"
									element={<AdAppsHome />}
								/>
								{RedirectRoute('/appRegistrationHome', '/adApps')}
								<Route
									path="/adApps/:id"
									element={<AdAppsDetail />}
								/>
								<Route 
									path="/cloudAccounts" 
									element={<CloudAccountsHome />}  
								/>
								{RedirectRoute('/cloudaccount', '/cloudAccounts')}
								<Route
									path="/account/reset/:token"
									element={<ConfirmPasswordReset />}
								/>
								<Route
									path="/cloudGroups"
									element={<CloudGroupsHome />}
								/>
								<Route
									path="/adApps/decide/:id"
									element={<DecideTransferAppOwnership />}
								/>
								<Route
									path="/adAppsImport"
									element={<AdAppsImport />}
								/>
								<Route
									path="/portOpeningRequests/connectedAzure"
									element={
										<PortOpeningRequestsHome
											subtitle="Connected Azure"
											scopes={[
												PortOpeningRequestsDirection.SubscriptionToOnPrem,
												PortOpeningRequestsDirection.SubscriptionToInternet,
												PortOpeningRequestsDirection.SubscriptionToSubscription,
											]}
										/>}
								/>

								<Route
									path="/doNotDecryptRequests"
									element={
										<DoNotDecryptRequestsHome
											subtitle=""
											scopes={[
												DoNotDecryptRequestsDirection.SubscriptionToInternet,
												DoNotDecryptRequestsDirection.SubscriptionToOnPrem,
												DoNotDecryptRequestsDirection.SubscriptionToSubscription,
											]}
										/>
									}
								/>
								<Route
									path="/subscriptions/:id/doNotDecryptRequests/new"
									element={<DoNotDecryptRequestsCreate />}
								/>
								<Route
									path="/subscriptions/:id/doNotDecryptRequests/connectedAzure"
									element={
										<DoNotDecryptRequestsHome
											scopes={[
												DoNotDecryptRequestsDirection.SubscriptionToOnPrem,
												DoNotDecryptRequestsDirection.SubscriptionToInternet,
												DoNotDecryptRequestsDirection.SubscriptionToSubscription,
											]}
										/>
									}
								/>
								<Route
									path="/subscriptions/:id/doNotDecryptRequestsHistory"
									element={<DoNotDecryptRequestsHistory />}
								/>

								<Route path="/doNotDecryptRequests/:id" element={<DoNotDecryptRequestsDetail />} />

								<Route
									path="/subscriptions/:subsId/portOpeningRequests/:porId"
									element={<PortOpeningRequestsDetail />}
								/>
								<Route
									path="/subscriptions"
									element={<SubscriptionsHome />}
								/>
								<Route
									path="/subscriptions/selfattestation"
									element={<SelfAttestation />}
								/>	
								<Route
									path="/subscriptions/new"
									element={<SubscriptionsRequest />}
								/>
								<Route
									path="/subscriptions/import"
									element={<SubscriptionsImport />}
								/>
								<Route
									path="/subscriptions/:id"
									element={<SubscriptionsDetail />}
								/>
								<Route
									path="/subscriptions/:id/adminConsent"
									element={<SubAdminConsent />}
								/>
								<Route
									path="/subscriptions/:subsId/portOpeningRequests"
									element={<PortOpeningRequestsHome
										scopes={[
											PortOpeningRequestsDirection.SubscriptionToOnPrem,
											PortOpeningRequestsDirection.SubscriptionToInternet,
											PortOpeningRequestsDirection.SubscriptionToSubscription,
										]}
									/>}
								/>
								<Route
									path="/OnPremPortOpeningRequests"
									element={<OnPremPortOpeningRequestsHome
										scopes={[
											PortOpeningRequestsDirection.DomainToConnection,
											PortOpeningRequestsDirection.DomainToInternet,
											PortOpeningRequestsDirection.DomainProxyToInternet
										]}
									/>}
								/>
								<Route
									path="/subscriptions/:subsId/portOpeningRequest/new"
									element={<PortOpeningRequestsCreate />}
								/>
								<Route
									path="/OnPremPortOpeningRequests/new"
									element={<OnPremPortOpeningRequestsCreate />}
								/>
								<Route
									path="/onPremPortOpeningRequests/:porId"
									element={<OnPremPortOpeningRequestsDetail />}
								/>
								<Route
									path="/subscriptions/:id/rules"
									element={<SubscriptionRulesTable />}
								/>
								{/*<Route
									path="/subscriptions/:id/policyExemptionRequests"
									element={<PolicyExemptionRequestsHome />}
										/>*/}
								<Route
									path="/subscriptions/:id/portOpeningRequestsHistory"
									element={<PortOpeningRequestsHistory />}
								/>
								{/*<Route
									path="/subscriptions/:id/policyExemptionRequests/new"
									element={<PolicyExemptionRequestsCreate />}
								/>
								<Route
									path="/subscriptions/:id/policyExemptionRequests/:perid"
									element={<PolicyExemptionRequestsDetail />}
									/>*/}

								<Route
									path="/feedback"
									element={<FeedbackHome />}
								/>
								<Route
									path="/organizations"
									element={<OrganizationsHome />}
								/>
								<Route
									path="/approvals"
									element={<ApprovalsHome />}
								/>
								<Route
									path="/subscriptions/:subsId/virtualMachineRequest/new"
									element={<VirtualMachineRequest />}
								/>
								<Route
									path="/subscriptions/:subsId/virtualMachineRequestHistory"
									element={<VirtualMachineRequestHistory />}
								/>
								<Route
									path="/subscriptions/:subsId/virtualMachineRequest/:vmrId"
									element={<VirtualMachineRequestDetail />}
								/>

								<Route
									path="/awsPortOpeningRequest/new"
									element={<ASWPortOpeningRequestsCreate/>}
								/>

								<Route
									path="/tap"
									element={<TemporaryAccessPassword />}
								/>

								<Route
									path="/awsPortOpeningRequest/connectedAWS"
									element={
										<AWSPortOpeningRequestsHome
											subtitle="Connected AWS"
											scopes={[
												PortOpeningRequestsDirection.AccountToInternet,
											]}
										/>}
								/>

								<Route
									path="/accounts/:accId/awsPortOpeningRequests/:porId"
									element={<AWSPortOpeningRequestsDetail/>}
								/>
							</Routes>
						</main>
					</Page.Body>
					<Page.Footer>
						<Footer />
						<div style={{ textAlign: 'center' }}><a href="https://beian.miit.gov.cn" style={{color: '#bbb'}} onMouseEnter={(e) => { (e.target as HTMLAnchorElement).style.color = 'black' }} onMouseOut={(e) => {(e.target as HTMLAnchorElement).style.color = '#bbb' }} target="_blank"> 沪ICP备09048291号-112 </a></div>
					</Page.Footer>
				</Page>
			</BrowserRouter>
		</ThemeProvider>
	);
};

export default App;
